import Typography from "aldoo-ra/Typography"
import React from "react"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import { VariantReader } from "aldoo-ra/VariantReader"
import Localization from "aldoo-ra/Localization"
import SitePlatform from "./SitePlatform"
import { twMerge } from "tailwind-merge"

/**
 * Helper function to render a platform
 * @param {object} data - The data containing platform details.
 * @param {string} platform - The platform name (e.g., "ios" or "android").
 * @returns {JSX.Element|null} A platform link with an icon and label, or null if platform not found.
 */
const renderPlatform = (data, platform) => {
  const platformData = data.items.find((item) => item.name === platform)
  if (!platformData) return null
  return (
    <a
      key={platformData.label}
      className="flex flex-row items-center gap-1 hover:text-gray_800 transition-colors"
      href={platformData.link[0].url}
      target="_blank"
      rel="noreferrer"
    >
      <DynamicSVG
        url={platformData.image}
        alt={platformData.label}
        className="w-6 h-6"
      />
      <span className="text-center">{platformData.label}</span>
    </a>
  )
}

/**
 * PlatformsAvailable Component
 * Renders a list of platforms available (e.g., iOS, Android, Windows).
 * @param {object} props - The component props.
 * @param {object} props.data - The data containing platform details.
 * @param {string} props.variant - Styling variant to apply different styles in different contexts.
 * @param {object} props.styleOverrides - Optional style overrides.
 * @param {string} props.className - Additional custom class names to apply.
 * @returns {JSX.Element}
 */
const PlatformsAvailable = ({
  data,
  variant = "default",
  styleOverrides,
  className = "",
  selectedPlatform,
}) => {
  const { tjsx } = Localization()

  // Use VariantReader to get styles based on variant - must be called before any early returns
  const variants = VariantReader.useVariant({
    componentName: "PlatformsAvailable",
    variant,
    styleOverrides,
  })

  // Early return if no platform data available
  if (!data?.platforms?.[0]?.items) {
    return null
  }

  //get the available names from the data.platforms[0].items.name
  const availablePlatformsFromCMS = data.platforms?.[0]?.items?.map(
    (item) => item.name
  ) || []
  const otherPlatforms = availablePlatformsFromCMS.filter(
    (item) => item !== selectedPlatform
  )

  // Create template data with platform components
  const templateData = otherPlatforms.reduce((acc, platform, idx) => {
    if (data.platforms?.[0]) {
      acc["platform_" + (idx + 1)] = renderPlatform(data.platforms[0], platform)
    }
    return acc
  }, {})

  // Get platform components and render them as keys to templateData
  const availablePlatforms = availablePlatformsFromCMS
    .map((platform) => {
      if (!data.platforms?.[0]) return null
      const platformElement = renderPlatform(data.platforms[0], platform)
      if (!platformElement) return null
      templateData[platform] = platformElement
      return platformElement
    })
    .filter(Boolean)

  // If no platforms available, return null
  if (availablePlatforms.length === 0) return null

  // Get the template from data.platforms[0].label only, no fallbacks
  const templateKey = data.platforms?.[0]?.label

  // Only split the template if there are more than 2 platforms
  let textPart = ""
  let platformsPart = ""
  const shouldUseMultiline = templateKey.match(/{{[^{}]+}}/g)?.length > 2

  if (shouldUseMultiline) {
    // Find the position where the first platform placeholder starts
    const firstPlaceholderPos = templateKey.indexOf("{{")
    if (firstPlaceholderPos > 0) {
      textPart = templateKey.substring(0, firstPlaceholderPos).trim()
      platformsPart = templateKey.substring(firstPlaceholderPos)
    }
  }

  // Combine variant classes with any additional custom classes using twMerge
  const finalClassName = twMerge(variants, className)

  return (
    <Typography component="div" variant="body1" className="my-0">
      {shouldUseMultiline ? (
        // Multi-line layout for more than 2 platforms
        <div className="flex flex-col md:flex-row">
          {textPart && (
            <span
              className={twMerge(
                finalClassName,
                "text-center md:text-left mb-2 md:mb-0"
              )}
            >
              {tjsx(textPart, {})}
            </span>
          )}
          {platformsPart && (
            <span className={finalClassName}>
              {tjsx(platformsPart, templateData)}
            </span>
          )}
        </div>
      ) : (
        // Single line for 2 or fewer platforms
        <span className={finalClassName}>
          {tjsx(templateKey, templateData)}
        </span>
      )}
    </Typography>
  )
}

export default PlatformsAvailable
